import {
  Box,
  Button,
  Select as _Select,
  MenuItem,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  TablePagination,
} from '@mui/material';
import { Description as DescriptionIcon, MoreVert, Gavel as _Gavel, Archive, Download, ExpandMore, ExpandLess, Close, Search } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Layout from '../components/Layout';
import CaseDialog from '../components/CaseDialog';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import UnderstaffingScheme from './UnderstaffingScheme';
import { CaseStatus, getCaseStatusSx } from '../utils/caseStatusStyles';

/* ───────────────── helpers ───────────────── */
const moneyCell =
  <T extends Record<string, any>>(field: keyof T) =>
  ({ row }: GridRenderCellParams<T>) => {
    const num = parseFloat(String(row[field]));
    const formatted = isNaN(num) ? '-' : `$${num.toLocaleString()}`;
    return (
      <Box
        sx={{
          fontWeight: 600,
        }}
      >
        {formatted}
      </Box>
    );
  };

const jsonArrayCell =
  (field: string) =>
  ({ row }: GridRenderCellParams<any>) => {
    const raw = row[field];
    const arr = Array.isArray(raw) ? raw : raw ? JSON.parse(String(raw)) : [];
    return arr.join(', ');
  };

const facilityCountCell =
  (field: string) =>
  ({ row }: GridRenderCellParams<any>) => {
    const raw = row[field];
    const arr = Array.isArray(raw) ? raw : raw ? JSON.parse(String(raw)) : [];
    const count = arr.length;
    const ccnList = arr.join(', ');
    
    return (
      <Tooltip title={count > 0 ? `CCNs: ${ccnList}` : 'No facilities'} arrow>
        <Chip
          label={`${count} ${count === 1 ? 'Facility' : 'Facilities'}`}
          size="small"
          variant="outlined"
          sx={{ cursor: 'help' }}
        />
      </Tooltip>
    );
  };

const dataGridBoldHeaders = {
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'grey.50',
    borderBottom: '2px solid',
    borderBottomColor: 'divider',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: 'grey.50',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    color: 'text.primary',
  },
  '& .MuiDataGrid-columnSeparator': {
    color: 'divider',
  },
};

/* ───────────────── component ───────────── */
export default function SchemeList() {
  const navigate = useNavigate();
  const { access, currentAccount } = useAuth();
  const isDemoMode = currentAccount?.demo_mode ?? false;
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);
  const [searchParams] = useSearchParams();
  const { schemeCode } = useParams<{ schemeCode?: string }>();

  const [schemes, setSchemes] = useState<any[]>([]);
  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [schemeDetails, setSchemeDetails] = useState<any>(null);

  const [view, setView] = useState<'affiliations' | 'facilities' | 'owners'>('owners');
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loadingRows, setLoadingRows] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Pagination state for owners view
  const [ownersPage, setOwnersPage] = useState(0);
  const [ownersPageSize, setOwnersPageSize] = useState(25);
  const [ownersPaginationData, setOwnersPaginationData] = useState<any>(null);

  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [defaultCaseValues, setDefaultCaseValues] = useState<any>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<any>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveNote, setArchiveNote] = useState('');
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [schemeDetailsExpanded, setSchemeDetailsExpanded] = useState(true);
  const [_fraudFormulaExpanded, _setFraudFormulaExpanded] = useState(false);
  const [openingInvestigation, setOpeningInvestigation] = useState(false);


  const month = '2023-06';

  /* ─ Fetch Schemes on mount ─ */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/schemes/', { headers });
        const data = res.data.results ?? res.data;
        setSchemes(data);

        // If no schemeCode in URL, redirect to first available scheme
        if (!schemeCode) {
          const defaultScheme = data[0];
          if (defaultScheme) {
            navigate(`/schemes/${defaultScheme.code}`, { replace: true });
          }
          return;
        }

        // Find scheme by code from URL
        const matchedScheme = data.find((s: any) => s.code === schemeCode);
        if (matchedScheme) {
          setSchemeId(matchedScheme.id);
        } else if (data.length > 0) {
          // Scheme not found or no access, redirect to first available
          navigate(`/schemes/${data[0].code}`, { replace: true });
        }
      } catch (err) {
        console.error('Failed to fetch schemes', err);
      }
    })();
  }, [access, headers, schemeCode, navigate]);

  /* ─ Fetch Scheme Details when schemeId changes ─ */
  useEffect(() => {
    if (!schemeId) return;
    (async () => {
      try {
        const res = await api.get(`/schemes/${schemeId}/`, { headers });
        setSchemeDetails(res.data);
      } catch (err) {
        console.error('Failed to fetch scheme details', err);
      }
    })();
  }, [schemeId, headers]);

  /* ─ Fetch Table Rows ─ */
  const fetchRows = async (isSearching = false) => {
    if (!schemeId) return;
    setLoadingRows(true);
    if (isSearching) setSearchLoading(true);
    try {
      const params: any = { month, view };
      if (search) params.search = search;

      // Add pagination parameters for owners view
      if (view === 'owners') {
        params.page = ownersPage + 1; // Convert 0-based to 1-based
        params.page_size = ownersPageSize;
        params.active_only = showActiveOnly;
      }

      const res = await api.get(`/schemes/${schemeId}/targets/`, { headers, params });

      // Small delay to ensure loading state is visible during pagination
      if (view === 'owners' && !isSearching) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Handle paginated response for owners view
      if (view === 'owners' && res.data.results !== undefined) {
        setOwnersPaginationData(res.data);
        setRows(res.data.results);
      } else {
        setRows(res.data.results ?? res.data);
        setOwnersPaginationData(null);
      }
    } catch (err) {
      console.error('Failed to fetch targets', err);
    } finally {
      setLoadingRows(false);
      if (isSearching) setSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeId, view]);

  // Debounced search effect
  useEffect(() => {
    if (search === '') {
      fetchRows(); // Immediate search for empty string
      return;
    }
    // Longer debounce for owners view due to complex data processing
    const debounceTime = view === 'owners' ? 800 : 500;
    const timeoutId = setTimeout(() => {
      fetchRows(true);
    }, debounceTime);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, view]); // Trigger search when search value or view changes

  // Trigger pagination for owners view when pagination state changes
  useEffect(() => {
    if (view === 'owners' && schemeId) {
      fetchRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownersPage, ownersPageSize]);

  // Reset pagination when switching to owners view
  useEffect(() => {
    if (view === 'owners') {
      setOwnersPage(0); // Reset to first page when switching to owners view
    }
  }, [view]);

  // Reset pagination when showActiveOnly changes for owners view
  useEffect(() => {
    if (view === 'owners') {
      setOwnersPage(0); // Reset to first page when filter changes
      fetchRows(); // Fetch new results immediately
    }
  }, [showActiveOnly, view]);

  // Reset pagination when search changes for owners view
  useEffect(() => {
    if (view === 'owners') {
      setOwnersPage(0); // Reset to first page when search changes
    }
  }, [search, view]);

  /* ─ Open Review ─ */
  const openInvestigation = async (targetId: string) => {
    try {
      setOpeningInvestigation(true);
      console.log('Opening investigation for target:', targetId);
      const res = await api.post(`/schemes/${schemeId}/targets/${targetId}/open_investigation/`, {}, { headers });

      console.log('Investigation response:', res.data);

      if (res.data.error) {
        console.error('Error from API:', res.data.error);
        alert(`Error: ${res.data.error}`);
        setOpeningInvestigation(false);
        return;
      }

      const caseId = res.data.case_id;
      const taskId = res.data.task_id;
      if (!caseId) {
        console.error('No case_id in response:', res.data);
        alert('Failed to create case - no case ID returned');
        setOpeningInvestigation(false);
        return;
      }

      if (res.data.existing) {
        console.log('Navigating to existing case:', caseId);
        // Navigate to existing case scoping
        navigate(`/cases/${caseId}`);
      } else {
        console.log('Navigating to new case:', caseId);
        // Navigate to new case with task_id for progress tracking
        const navUrl = taskId ? `/cases/${caseId}?task_id=${taskId}` : `/cases/${caseId}`;
        navigate(navUrl);
        // Show success message
        console.log(res.data.message);
      }

      // Refresh the targets list to update status
      await fetchRows();
    } catch (err: any) {
      console.error('Failed to open investigation:', err);
      console.error('Error details:', err.response?.data);
      alert(`Failed to open investigation: ${err.response?.data?.error || err.message}`);
      setOpeningInvestigation(false);
    }
  };

  /* ─ Archive Target ─ */
  const archiveTarget = async () => {
    if (!selectedTarget || !archiveNote.trim()) return;
    setArchiveLoading(true);
    try {
      await api.post(`/schemes/${schemeId}/targets/${selectedTarget.id}/archive/`, {
        closure_note: archiveNote
      }, { headers });
      // Refresh the targets list
      await fetchRows();
      // Close dialogs and reset state
      setArchiveDialogOpen(false);
      setMenuAnchorEl(null);
      setSelectedTarget(null);
      setArchiveNote('');
    } catch (err) {
      console.error('Failed to archive target', err);
    } finally {
      setArchiveLoading(false);
    }
  };

  /* ─ Unarchive Target ─ */
  const unarchiveTarget = async (target: any) => {
    try {
      await api.post(`/schemes/${schemeId}/targets/${target.id}/unarchive/`, {}, { headers });
      // Refresh the targets list
      await fetchRows();
      // Close menu
      setMenuAnchorEl(null);
      setSelectedTarget(null);
    } catch (err) {
      console.error('Failed to unarchive target', err);
    }
  };

  /* ─ Fetch Templates ─ */
  const [templates, setTemplates] = useState<any[]>([]);
  useEffect(() => {
    if (!schemeId) return;
    (async () => {
      try {
        const res = await api.get(`/schemes/${schemeId}/templates/`, { headers });
        setTemplates(res.data);
      } catch (err) {
        console.error('Failed to fetch templates', err);
      }
    })();
  }, [schemeId, headers]);

  /* ─ Action Handlers ─ */
  const handleViewCase = useCallback((caseId: string) => {
    navigate(`/cases/${caseId}`);
  }, [navigate]);

  const handleCreateCase = useCallback((row: any, isAffiliation: boolean = true) => {
    if (isAffiliation) {
      const raw = row.ccns;
      const _ccns = Array.isArray(raw) ? raw : raw ? JSON.parse(raw) : [];
      const affiliationName = row.affiliation_name;

      setDefaultCaseValues({
        name: affiliationName,
        status: 'OPEN',
        lead_defendant: affiliationName,
        origin_target: row.id,
        scheme_id: schemeId,
      });
    } else {
      const _ccn = row.ccn;
      const orgName = row.org_name;

      setDefaultCaseValues({
        affiliation_name: orgName,
        file_month: month,
        name: orgName,
        status: 'OPEN',
        lead_defendant: orgName,
        origin_target: row.id,
        scheme_id: schemeId,
      });
    }
    setCaseModalOpen(true);
    setMenuAnchorEl(null);
    setSelectedTarget(null);
  }, [schemeId, month]);

  const handleArchiveClick = useCallback((row: any) => {
    setSelectedTarget(row);
    setArchiveDialogOpen(true);
    setMenuAnchorEl(null);
  }, []);

  /* ─ Download Template ─ */
  const handleDownloadTemplate = useCallback(async (item: any) => {
    if (item.is_auto_generated || !item.filename) {
      console.warn('Cannot download auto-generated templates');
      return;
    }
    
    try {
      const response = await api.get(`/schemes/${schemeId}/templates/${item.id}/download/`, {
        headers,
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', item.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template', err);
    }
  }, [schemeId, headers]);

  /* ─ Columns ─ */
  const affiliationCols: GridColDef[] = useMemo(() => [
    { field: 'affiliation_name', headerName: 'Affiliation', flex: 1 },
    { field: 'total_billed', headerName: 'Total Billed', width: 160, renderCell: moneyCell('total_billed') },
    { field: 'total_paid',   headerName: 'Total Paid',   width: 160, renderCell: moneyCell('total_paid') },
    { field: 'states',       headerName: 'States',       width: 120, renderCell: jsonArrayCell('states') },
    { field: 'ccns',         headerName: '# of Facilities', width: 140, renderCell: facilityCountCell('ccns') },
    {
      field: 'create_case',
      headerName: '',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const hasActiveCase = row.active_case_id;
        const isClosed = row.status === 'CLOSED';
        const hasConflict = row.has_ccn_conflict;
        const canOpenInvestigation = row.can_open_investigation;

        // Show specific button based on state
        if (hasActiveCase) {
          const statusSx = getCaseStatusSx(row.active_case_status as CaseStatus);
          return (
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleViewCase(row.active_case_id);
              }}
              sx={{
                fontWeight: 500,
                border: 'none',
                ...statusSx
              }}
            >
              View Case
            </Button>
          );
        }

        // Show View Case for CCN conflicts (only if no direct case)
        if (hasConflict && !hasActiveCase && row.conflicting_case && row.conflicting_case.id !== row.any_case_id) {
          const tooltipText = row.conflicting_case.lead_defendant || row.conflicting_case.name
            ? `This Lead contains facilities in an investigation. Case: ${row.conflicting_case.lead_defendant || row.conflicting_case.name}`
            : "This Lead contains facilities in an investigation";

          return (
            <Tooltip title={tooltipText} arrow>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewCase(row.conflicting_case.id);
                }}
                sx={{
                  fontWeight: 500,
                  border: 'none',
                  ...(row.conflicting_case.status === 'CLOSED' || row.conflicting_case.status === 'ARCHIVED'
                    ? { backgroundColor: '#d32f2f', color: 'white', '&:hover': { backgroundColor: '#c62828' } }
                    : getCaseStatusSx(row.conflicting_case.status as CaseStatus)
                  )
                }}
              >
                View Case
              </Button>
            </Tooltip>
          );
        }

        if (isClosed) {
          const hasArchivedCase = row.any_case_id && row.any_case_status === 'CLOSED';

          if (hasArchivedCase) {
            // Show only View Case button for archived targets with cases
            return (
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewCase(row.any_case_id);
                }}
              >
                View Case
              </Button>
            );
          } else {
            // Show only Unarchive button for archived targets without cases
            const unarchiveButton = (
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  unarchiveTarget(row);
                }}
              >
                Unarchive
              </Button>
            );

            return row.closure_note ? (
              <Tooltip title={`Archived: ${row.closure_note}`} arrow>
                {unarchiveButton}
              </Tooltip>
            ) : unarchiveButton;
          }
        }

        // For open targets, show Investigate button (demo mode) or Actions menu (normal mode)
        if (isDemoMode) {
          return (
            <Button
              variant="outlined"
              size="small"
              onClick={async (e) => {
                e.stopPropagation();
                await openInvestigation(row.id);
              }}
              disabled={!canOpenInvestigation}
              sx={{
                opacity: canOpenInvestigation ? 1 : 0.6,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.main',
                },
              }}
            >
              Investigate
            </Button>
          );
        }

        // Normal mode: show actions menu
        return (
          <>
            <Button
              variant="outlined"
              size="small"
              endIcon={<MoreVert />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTarget(row);
                setMenuAnchorEl(e.currentTarget);
              }}
              disabled={!canOpenInvestigation}
              sx={{
                opacity: canOpenInvestigation ? 1 : 0.6,
                backgroundColor: canOpenInvestigation ? 'inherit' : 'rgba(0, 0, 0, 0.04)'
              }}
            >
              Actions
            </Button>

            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl) && selectedTarget?.id === row.id}
              onClose={() => {
                setMenuAnchorEl(null);
                setSelectedTarget(null);
              }}
            >
              <MenuItem
                onClick={async (e) => {
                  e.stopPropagation();
                  setMenuAnchorEl(null);
                  await openInvestigation(row.id);
                }}
                disabled={!canOpenInvestigation}
              >
                <ListItemIcon>
                  <Search fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Open Review" />
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchiveClick(row);
                }}
              >
                <ListItemIcon>
                  <Archive fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Archive Lead" />
              </MenuItem>
            </Menu>
          </>
        );
      },
    }
  ], [handleViewCase, unarchiveTarget, handleArchiveClick, selectedTarget, menuAnchorEl, schemeId, isDemoMode]);

  const facilityCols: GridColDef[] = useMemo(() => [
    { field: 'ccn', headerName: 'CCN', width: 100 },
    { field: 'state', headerName: 'State', width: 80 },
    { field: 'org_name', headerName: 'Organization', flex: 1 },
    { field: 'dba_name', headerName: 'DBA', flex: 1 },
    { field: 'total_billed', headerName: 'Total Billed', width: 160, renderCell: moneyCell('total_billed') },
    { field: 'total_paid',   headerName: 'Total Paid',   width: 160, renderCell: moneyCell('total_paid') },
    {
      field: 'create_case',
      headerName: '',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const hasActiveCase = row.active_case_id;
        const isClosed = row.status === 'CLOSED';
        const hasConflict = row.has_ccn_conflict;
        const canOpenInvestigation = row.can_open_investigation;

        // Show specific button based on state
        if (hasActiveCase) {
          const statusSx = getCaseStatusSx(row.active_case_status as CaseStatus);
          return (
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleViewCase(row.active_case_id);
              }}
              sx={{
                fontWeight: 500,
                border: 'none',
                ...statusSx
              }}
            >
              View Case
            </Button>
          );
        }

        // Show View Case for CCN conflicts (only if no direct case)
        if (hasConflict && !hasActiveCase && row.conflicting_case && row.conflicting_case.id !== row.any_case_id) {
          const tooltipText = row.conflicting_case.lead_defendant || row.conflicting_case.name
            ? `This Lead contains facilities in an investigation. Case: ${row.conflicting_case.lead_defendant || row.conflicting_case.name}`
            : "This Lead contains facilities in an investigation";

          return (
            <Tooltip title={tooltipText} arrow>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewCase(row.conflicting_case.id);
                }}
                sx={{
                  fontWeight: 500,
                  border: 'none',
                  ...(row.conflicting_case.status === 'CLOSED' || row.conflicting_case.status === 'ARCHIVED'
                    ? { backgroundColor: '#d32f2f', color: 'white', '&:hover': { backgroundColor: '#c62828' } }
                    : getCaseStatusSx(row.conflicting_case.status as CaseStatus)
                  )
                }}
              >
                View Case
              </Button>
            </Tooltip>
          );
        }

        if (isClosed) {
          const hasArchivedCase = row.any_case_id && row.any_case_status === 'CLOSED';

          if (hasArchivedCase) {
            // Show only View Case button for archived targets with cases
            return (
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewCase(row.any_case_id);
                }}
              >
                View Case
              </Button>
            );
          } else {
            // Show only Unarchive button for archived targets without cases
            const unarchiveButton = (
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  unarchiveTarget(row);
                }}
              >
                Unarchive
              </Button>
            );

            return row.closure_note ? (
              <Tooltip title={`Archived: ${row.closure_note}`} arrow>
                {unarchiveButton}
              </Tooltip>
            ) : unarchiveButton;
          }
        }

        // For open targets, show Investigate button (demo mode) or Actions menu (normal mode)
        if (isDemoMode) {
          return (
            <Button
              variant="outlined"
              size="small"
              onClick={async (e) => {
                e.stopPropagation();
                await openInvestigation(row.id);
              }}
              disabled={!canOpenInvestigation}
              sx={{
                opacity: canOpenInvestigation ? 1 : 0.6,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.main',
                },
              }}
            >
              Investigate
            </Button>
          );
        }

        // Normal mode: show actions menu
        return (
          <>
            <Button
              variant="outlined"
              size="small"
              endIcon={<MoreVert />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTarget(row);
                setMenuAnchorEl(e.currentTarget);
              }}
              disabled={!canOpenInvestigation}
              sx={{
                opacity: canOpenInvestigation ? 1 : 0.6,
                backgroundColor: canOpenInvestigation ? 'inherit' : 'rgba(0, 0, 0, 0.04)'
              }}
            >
              Actions
            </Button>

            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl) && selectedTarget?.id === row.id}
              onClose={() => {
                setMenuAnchorEl(null);
                setSelectedTarget(null);
              }}
            >
              <MenuItem
                onClick={async (e) => {
                  e.stopPropagation();
                  setMenuAnchorEl(null);
                  await openInvestigation(row.id);
                }}
                disabled={!canOpenInvestigation}
              >
                <ListItemIcon>
                  <Search fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Open Review" />
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchiveClick(row);
                }}
              >
                <ListItemIcon>
                  <Archive fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Archive Lead" />
              </MenuItem>
            </Menu>
          </>
        );
      },
    }
  ], [handleViewCase, unarchiveTarget, handleCreateCase, handleArchiveClick, selectedTarget, menuAnchorEl, isDemoMode]);

  const ownersCols: GridColDef[] = useMemo(() => [
    { 
      field: 'name', 
      headerName: 'Ownership Group', 
      flex: 1,
      renderCell: ({ row }) => {
        const ownerGroups = row.metadata?.ownership?.owner_groups || [];
        const leadGroup = ownerGroups.find((g: any) => g.role_suggestion === 'LEAD') || ownerGroups[0];
        const totalOwners = ownerGroups.reduce((sum: number, group: any) => sum + (group.members?.length || 0), 0);
        const isExpanded = expandedRows.has(row.id);
        
        console.log('Owner row debug:', {
          rowId: row.id,
          isExpanded,
          expandedRowsSize: expandedRows.size,
          ownerGroupsLength: ownerGroups.length,
          hasOwnerGroups: ownerGroups.length > 0,
          totalOwners,
          leadGroupMembers: leadGroup?.members?.length || 0
        });
        
        // Parse the portfolio name to extract individual names
        const portfolioName = row.name || '';
        const leadNames = portfolioName.split(' | ').filter((name: string) => name && !name.includes('+'));
        const hasMore = portfolioName.includes('+') && portfolioName.includes('more');
        
        const toggleExpand = (e: React.MouseEvent) => {
          e.stopPropagation();
          console.log('Toggle expand clicked for row:', row.id, 'current expanded:', isExpanded);
          const newExpanded = new Set(expandedRows);
          if (isExpanded) {
            newExpanded.delete(row.id);
          } else {
            newExpanded.add(row.id);
          }
          console.log('New expanded set:', newExpanded);
          setExpandedRows(newExpanded);
        };
        
        return (
          <Box sx={{ py: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              {/* Hide expand button in demo mode */}
              {!isDemoMode && (
                <IconButton
                  size="small"
                  onClick={toggleExpand}
                  sx={{ mt: -0.5, flexShrink: 0 }}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {leadNames.length > 0 ? (
                  <>
                    {leadNames.map((name: string, index: number) => (
                      <Typography key={index} variant="body2" sx={{ fontWeight: 'medium', lineHeight: 1.4 }}>
                        {name}
                      </Typography>
                    ))}
                    {hasMore && (
                      <Typography variant="body2" sx={{ fontWeight: 'medium', lineHeight: 1.4, fontStyle: 'italic' }}>
                        {portfolioName.match(/\+\d+ more/)?.[0] || ''}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                    {portfolioName}
                  </Typography>
                )}
                {/* Hide owner counts in demo mode */}
                {!isDemoMode && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {totalOwners} {totalOwners === 1 ? 'Owner' : 'Owners'} • {leadGroup?.members?.length || 0} Lead
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Hide collapsible owner details in demo mode */}
            {!isDemoMode && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 2, pl: 4, borderLeft: '2px solid', borderLeftColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Owner Groups
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {ownerGroups.map((group: any, groupIndex: number) => (
                      <Box key={groupIndex} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={group.role_suggestion === 'LEAD' ? 'Lead Group' : 'Other Group'}
                            size="small"
                            color={group.role_suggestion === 'LEAD' ? 'primary' : 'default'}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {group.coverage_count} of {row.metadata?.ccns?.length} facilities
                            ({Math.round((group.coverage_pct || 0) * 100)}% coverage)
                          </Typography>
                        </Box>
                        {group.members?.map((member: any, memberIndex: number) => (
                          <ListItem key={memberIndex} sx={{ py: 0.5, pl: 2 }}>
                            <MuiListItemText
                              primary={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {member.owner?.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {member.owner?.type === 'I' ? 'Individual' : 'Organization'} •
                                    ID: {member.owner?.id}
                                  </Typography>
                                  {member.owner?.address_line_1_owner && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {[
                                        member.owner.address_line_1_owner,
                                        member.owner.address_line_2_owner,
                                        member.owner.city_owner,
                                        member.owner.state_owner,
                                        member.owner.zip_code_owner
                                      ].filter(Boolean).join(', ')}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </Box>
                    ))}
                  </List>
                </Box>
              </Collapse>
            )}
          </Box>
        );
      }
    },
    { 
      field: 'metadata.states', 
      headerName: 'States', 
      width: 120, 
      renderCell: ({ row }) => {
        const states = row.metadata?.states || [];
        return Array.isArray(states) ? states.join(', ') : states;
      }
    },
    { 
      field: 'metadata.ccns', 
      headerName: '# of Facilities', 
      width: 140, 
      renderCell: ({ row }) => {
        const ccns = row.metadata?.ccns || [];
        const count = Array.isArray(ccns) ? ccns.length : 0;
        const ccnList = Array.isArray(ccns) ? ccns.join(', ') : '';
        
        return (
          <Tooltip title={count > 0 ? `CCNs: ${ccnList}` : 'No facilities'} arrow>
            <Chip
              label={`${count} ${count === 1 ? 'Facility' : 'Facilities'}`}
              size="small"
              variant="outlined"
              sx={{ cursor: 'help' }}
            />
          </Tooltip>
        );
      }
    },
    { field: 'total_billed', headerName: 'Total Billed', width: 160, renderCell: moneyCell('total_billed') },
    { field: 'total_paid', headerName: 'Total Paid', width: 160, renderCell: moneyCell('total_paid') },
    {
      field: 'create_case',
      headerName: '',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        // In demo mode, always show Investigate button regardless of case status
        if (isDemoMode) {
          return (
            <Button
              variant="outlined"
              size="small"
              onClick={async (e) => {
                e.stopPropagation();
                await openInvestigation(row.id);
              }}
              sx={{
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.main',
                },
              }}
            >
              Investigate
            </Button>
          );
        }

        // Normal mode: show actions menu
        return (
          <>
            <Button
              variant="outlined"
              size="small"
              endIcon={<MoreVert />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTarget(row);
                setMenuAnchorEl(e.currentTarget);
              }}
            >
              Actions
            </Button>

            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl) && selectedTarget?.id === row.id}
              onClose={() => {
                setMenuAnchorEl(null);
                setSelectedTarget(null);
              }}
            >
              <MenuItem
                onClick={async (e) => {
                  e.stopPropagation();
                  setMenuAnchorEl(null);
                  await openInvestigation(row.id);
                }}
              >
                <ListItemIcon>
                  <Search fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Open Review" />
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchiveClick(row);
                }}
              >
                <ListItemIcon>
                  <Archive fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Archive Lead" />
              </MenuItem>
            </Menu>
          </>
        );
      },
    }
  ], [handleViewCase, unarchiveTarget, handleArchiveClick, selectedTarget, menuAnchorEl, schemeId, expandedRows, isDemoMode]);

  const columns = view === 'affiliations' ? affiliationCols : view === 'facilities' ? facilityCols : ownersCols;
  
  // Filter rows based on active cases toggle
  const filteredRows = showActiveOnly
    ? rows.filter(row => row.active_case_id || row.has_ccn_conflict || row.conflicting_case)
    : rows;

  // Show loading while redirecting (no schemeCode means we're about to redirect)
  if (!schemeCode) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  // snf-understaffing is now handled by dedicated routes in App.tsx
  // (NationalLandscape at /schemes/snf-understaffing, UnderstaffingScheme at /schemes/snf-understaffing/state/:stateCode)
  // This path shouldn't be reached, but redirect just in case
  if (schemeCode === 'snf-understaffing') {
    window.location.href = '/schemes/snf-understaffing';
    return null;
  }

  return (
    <Layout>
      {/* ───── Scheme Selector ───── */}
      {/* Commented out - scheme selection now handled via navigation dropdown
      <Card sx={{ 
        mb: 4, 
        backgroundColor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2
      }}>
        <CardContent sx={{ py: 3, px: 3 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', minWidth: 'fit-content', display: 'flex', alignItems: 'center', gap: 1 }}>
              📋 Select Scheme:
            </Typography>
            <Select
              value={schemeId || ''}
              onChange={(e) => setSchemeId(e.target.value)}
              displayEmpty
              variant="outlined"
              sx={{ 
                minWidth: 300,
                backgroundColor: 'background.paper',
                '& .MuiSelect-select': {
                  py: 1.5,
                  fontWeight: 500
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '2px'
                }
              }}
            >
              <MenuItem value="" disabled>
                <em>Choose a scheme to analyze...</em>
              </MenuItem>
              {schemes.map((s) => {
                const isForthcoming = s.metadata?.status === 'forthcoming';
                return (
                  <MenuItem 
                    key={s.id} 
                    value={s.id}
                    disabled={isForthcoming}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      opacity: isForthcoming ? 0.6 : 1,
                    }}
                  >
                    <span>{s.name}</span>
                    {isForthcoming && (
                      <Chip
                        label="Coming Soon"
                        size="small"
                        color="default"
                        sx={{ ml: 2, height: 20 }}
                      />
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        </CardContent>
      </Card>
      */}

      {/* ───── Highlighted Scheme Details Section ───── */}
      {schemeDetails && (
        <Box
          mb={4}
          sx={{
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box 
            p={3}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              transition: 'background-color 0.2s',
            }}
            onClick={() => setSchemeDetailsExpanded(!schemeDetailsExpanded)}
          >
            <Box flex={1} display="flex" alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {schemeDetails.name}
              </Typography>
            </Box>
            <Box display="flex" gap={3} alignItems="center">
              <Box display="flex" flexDirection="column" alignItems="flex-start">
                <Typography variant="body2" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                  Total Billed
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {schemeDetails.metadata?.total_billed || '$0'}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="flex-start">
                <Typography variant="body2" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                  Total Paid by Medicare
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {schemeDetails.metadata?.total_paid || '$0'}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              sx={{
                color: 'inherit',
                ml: 4,
              }}
            >
              {schemeDetailsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          
          <Collapse in={schemeDetailsExpanded} timeout="auto">
            <Box px={3} pb={3}>
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                {/* Left 1/2 - Description */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 600, mb: 1 }}>
                    Scheme Summary
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, whiteSpace: 'pre-line' }}>
                    {schemeDetails.description}
                  </Typography>
                </Box>

                <Box sx={{
                  width: '1px',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  mx: 2
                }} />

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 600, mb: 1 }}>
                    Fraud Identification Signals
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                    On a single day:
                  </Typography>
                  <Box component="ol" sx={{
                    pl: 3,
                    m: 0,
                    '& li': {
                      mb: 1.5,
                      fontSize: '1rem',
                      lineHeight: 1.5,
                      opacity: 0.9
                    }
                  }}>
                    <li>A facility bulk-admitted over 10% of its patient population to SNF care.</li>
                    <li>The bulk admission occurred after July 21, 2020: one month after CMS's guidance cautioning against misbilling of SNF care.</li>
                    <li>The facility did not admit a comparable number of patients after the three-day-rule was reinstated on May 11, 2023.</li>
                    <li>No resident deaths attributed to COVID within three weeks of the bulk admit date.</li>
                    <li>The highest number of residents reported with COVID, within three weeks of the bulk admit date, was 25% of the total number of residents bulk admitted to SNF care.</li>
                  </Box>
                </Box>
              </Box>

              {/* Financial totals for three-day-covid-policy */}

              {/* Fraud Identification Formula for three-day-covid-policy - HIDDEN */}
              {/* {schemeDetails.code === 'three-day-covid-policy' && (
                <Box sx={{ mb: 4, display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.2)', overflow: 'hidden' }}>
                    <Box
                      sx={{
                        p: 3,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                        transition: 'background-color 0.2s',
                      }}
                      onClick={() => setFraudFormulaExpanded(!fraudFormulaExpanded)}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Fraud Identification Signals
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{
                          color: 'text.primary',
                        }}
                      >
                        {fraudFormulaExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>

                    <Collapse in={fraudFormulaExpanded} timeout="auto">
                      <Box sx={{ px: 3, pb: 3 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Conditions for identifying an instance of the fraud:
                          </Typography>

                          <Box sx={{ mb: 3, ml: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              1. Bulk Admissions:
                            </Typography>
                            <Box component="ul" sx={{ ml: 2, mt: 0.5 }}>
                              <Typography component="li" variant="body1" sx={{ mb: 0.5, opacity: 0.9 }}>
                                The number of residents elevated to SNF care on the date in question exceeded 10% of
                                the total number of residents present at the facility on that date. AND
                              </Typography>
                              <Typography component="li" variant="body1" sx={{ opacity: 0.9 }}>
                                The facility never admitted a comparable volume of residents to SNF care when the
                                three-day rule was in effect.
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ mb: 3, ml: 2 }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              <Box component="span" sx={{ fontWeight: 'bold' }}>2. Notice:</Box>{' '}
                              The date in question fell between:
                            </Typography>
                            <Typography variant="body1" sx={{ ml: 2, opacity: 0.9 }}>
                              <Typography component="span" sx={{ fontStyle: 'italic' }}>July 21, 2020</Typography>: one month after CMS clarified that COVID-positive status alone does not
                              justify SNF admission; and<br />
                              <Typography component="span" sx={{ fontStyle: 'italic' }}>May 11, 2023</Typography>: when the three-day hospitalization requirement was reinstated.
                            </Typography>
                          </Box>

                          <Box sx={{ mb: 3, ml: 2 }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              <Box component="span" sx={{ fontWeight: 'bold' }}>3. Lack of Outbreak Conditions:</Box>{' '}
                              The highest number of residents reported with COVID-19 at
                              the facility in the three weeks before and after the date in question was both:
                            </Typography>
                            <Box component="ol" sx={{ ml: 2, mt: 0.5 }}>
                              <Typography component="li" variant="body1" sx={{ mb: 0.5, opacity: 0.9 }}>
                                Less than 25% of the number of residents elevated to SNF care on that date; and
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                  
                  <Box display="flex" flexDirection="row" gap={2} sx={{ minWidth: 400 }}>
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                      <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Total Suspected Fraudulently Billed
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {schemeDetails.metadata?.total_billed || '$0'}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                      <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Total Paid by Medicare
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {schemeDetails.metadata?.total_paid || '$0'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              
              {/* Show totals below if not three-day-covid-policy scheme */}
              {schemeDetails.code !== 'three-day-covid-policy' && (
                <Box display="flex" gap={4} alignItems="center">
                  <Box display="flex" flexDirection="column">
                    <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Total Billed
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {schemeDetails.metadata?.total_billed || '$0'}
                    </Typography>
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Total Paid by Medicare
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {schemeDetails.metadata?.total_paid || '$0'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      )}


      {/* ───── Side-by-side: Targets and Documents ───── */}
      <Box display="flex" gap={3} sx={{ mb: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Left: Target Analysis & Case Management */}
        <Card sx={{ flex: '1 1 65%' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  🎯 Leads
                </Typography>
              </Box>
              <Box display="flex" gap={2} alignItems="center">
                {/* Hide Active Cases Only toggle in demo mode */}
                {!isDemoMode && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showActiveOnly}
                        onChange={(e) => setShowActiveOnly(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active Cases Only"
                  />
                )}
{/* Hide tab buttons entirely in demo mode - only owners view is shown */}
                {!isDemoMode && (
                  <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={(_, val) => {
                      if (val) {
                        setSearch(''); // Clear search when switching views
                        setExpandedRows(new Set()); // Clear expanded rows when switching views
                        setView(val);
                      }
                    }}
                    size="small"
                  >
                    <ToggleButton value="owners">Owners</ToggleButton>
                    <ToggleButton value="affiliations">Affiliations</ToggleButton>
                    <ToggleButton value="facilities">Facilities</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />
            
            {/* Search Bar */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder={`Search ${view}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchRows(true)}
                sx={{ minWidth: 300 }}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                  endAdornment: searchLoading ? (
                    <CircularProgress size={20} sx={{ color: 'text.secondary' }} />
                  ) : search ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch('');
                        fetchRows();
                      }}
                      sx={{ color: 'text.secondary' }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  ) : null
                }}
              />
            </Box>
            
            {/* Data Grid or Custom Table */}
            {view === 'owners' ? (
              <TableContainer
                component={Paper}
                data-testid="owners-table-container"
                sx={{
                  height: 700,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  position: 'relative',
                  overflow: 'auto',
                }}
              >
                {loadingRows && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      zIndex: 10,
                    }}
                  >
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary">
                        Loading ownership groups...
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Table stickyHeader sx={{
                  minWidth: 650,
                  // DEMO EDIT (2026-05-20): neutralize action buttons — white/outlined look, grey on hover (no status colors).
                  // Remove this '& .MuiButton-root' block to restore the green/gold/red status button colors after the demo.
                  '& .MuiButton-root': {
                    backgroundColor: '#ffffff !important',
                    color: 'rgba(0, 0, 0, 0.75) !important',
                    border: '1px solid rgba(0, 0, 0, 0.23) !important',
                    boxShadow: 'none !important',
                  },
                  '& .MuiButton-root:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06) !important',
                    border: '1px solid rgba(0, 0, 0, 0.4) !important',
                    boxShadow: 'none !important',
                  },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', backgroundColor: 'grey.100' }}>Ownership Group</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 120, backgroundColor: 'grey.100' }}>States</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 140, backgroundColor: 'grey.100' }}># of Facilities</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 160, backgroundColor: 'grey.100' }}>Total Billed</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 160, backgroundColor: 'grey.100' }}>Total Paid</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 140, backgroundColor: 'grey.100' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row, index) => {
                      const ownerGroups = row.metadata?.ownership?.owner_groups || [];
                      const _leadGroup = ownerGroups.find((g: any) => g.role_suggestion === 'LEAD') || ownerGroups[0];
                      const totalOwners = ownerGroups.reduce((sum: number, group: any) => sum + (group.members?.length || 0), 0);
                      const isExpanded = expandedRows.has(row.id);
                      
                      // Parse the portfolio name to extract individual names
                      const portfolioName = row.name || '';
                      const leadNames = portfolioName.split(' | ').filter((name: string) => name && !name.includes('+'));
                      const hasMore = portfolioName.includes('+') && portfolioName.includes('more');
                      
                      const toggleExpand = () => {
                        const newExpanded = new Set(expandedRows);
                        if (isExpanded) {
                          newExpanded.delete(row.id);
                        } else {
                          newExpanded.add(row.id);
                        }
                        setExpandedRows(newExpanded);
                      };

                      const hasActiveCase = row.active_case_id;
                      const isClosed = row.status === 'CLOSED';
                      // DEMO EDIT (2026-05-20): these only fed the disabled row-coloring block — restore together with it after demo
                      // const hasConflict = row.has_ccn_conflict && !hasActiveCase && row.conflicting_case && row.conflicting_case.id !== row.any_case_id; // Only consider it a conflict if no direct case and conflicting case is different from own case
                      // const canOpenInvestigation = row.can_open_investigation;
                      // const hasClosedCase = row.any_case_id && row.any_case_status === 'CLOSED';

                      return (
                        <>
                          <TableRow
                            key={row.id}
                            sx={{
                              // DEMO EDIT (2026-05-20): case-status row coloring disabled for demo — use plain alternating rows.
                              // Restore the ORIGINAL backgroundColor / opacity / &:hover block below after the demo.
                              backgroundColor: index % 2 === 0 ? 'grey.25' : 'inherit',
                              '&:hover': {
                                backgroundColor: 'grey.100',
                              },
                              /* ORIGINAL (restore after demo):
                              // In demo mode, use simple alternating row colors without case status highlighting
                              backgroundColor: isDemoMode
                                ? (index % 2 === 0 ? 'grey.25' : 'inherit')
                                : hasActiveCase
                                  ? (() => {
                                      // Use case status for background color
                                      if (row.active_case_status === 'FILED') return 'rgba(76, 175, 80, 0.08)'; // light green
                                      if (row.active_case_status === 'UNDER_INVESTIGATION') return 'rgba(218, 165, 32, 0.15)'; // light gold
                                      if (row.active_case_status === 'IN_REVIEW') return 'rgba(255, 152, 0, 0.08)'; // light orange
                                      if (row.active_case_status === 'CLOSED' || row.active_case_status === 'ARCHIVED') return 'rgba(244, 67, 54, 0.08)'; // light red
                                      return 'rgba(33, 150, 243, 0.08)'; // fallback blue
                                    })()
                                  : (() => {
                                      // Handle CCN conflicts - use the conflicting case's status for highlighting
                                      if (hasConflict && row.conflicting_case) {
                                        const conflictingCaseStatus = row.conflicting_case.status;
                                        if (conflictingCaseStatus === 'FILED') return 'rgba(76, 175, 80, 0.08)'; // light green
                                        if (conflictingCaseStatus === 'UNDER_INVESTIGATION') return 'rgba(218, 165, 32, 0.15)'; // light yellow
                                        if (conflictingCaseStatus === 'CLOSED' || conflictingCaseStatus === 'ARCHIVED') return 'rgba(244, 67, 54, 0.08)'; // light red
                                        return 'rgba(244, 67, 54, 0.08)'; // fallback red for conflicts
                                      }
                                      // Check for closed cases or targets with closed cases
                                      if (isClosed || (hasClosedCase && !canOpenInvestigation)) {
                                        return 'rgba(244, 67, 54, 0.08)'; // light red for closed/archived
                                      }
                                      return index % 2 === 0
                                        ? 'grey.25'
                                        : 'inherit';
                                    })(),
                              opacity: isDemoMode ? 1 : (hasConflict ? 0.6 : 1), // No opacity changes in demo mode
                              '&:hover': {
                                backgroundColor: isDemoMode
                                  ? 'grey.100'
                                  : hasActiveCase
                                    ? (() => {
                                        // Use case status for hover color
                                        if (row.active_case_status === 'FILED') return 'rgba(76, 175, 80, 0.12)'; // light green
                                        if (row.active_case_status === 'UNDER_INVESTIGATION') return 'rgba(218, 165, 32, 0.25)'; // light gold
                                        if (row.active_case_status === 'IN_REVIEW') return 'rgba(255, 152, 0, 0.12)'; // light orange
                                        if (row.active_case_status === 'CLOSED' || row.active_case_status === 'ARCHIVED') return 'rgba(244, 67, 54, 0.12)'; // light red
                                        return 'rgba(33, 150, 243, 0.12)'; // fallback blue
                                      })()
                                    : (() => {
                                        // Handle CCN conflicts - use the conflicting case's status for hover highlighting
                                        if (hasConflict && row.conflicting_case) {
                                          const conflictingCaseStatus = row.conflicting_case.status;
                                          if (conflictingCaseStatus === 'FILED') return 'rgba(76, 175, 80, 0.12)'; // light green
                                          if (conflictingCaseStatus === 'UNDER_INVESTIGATION') return 'rgba(218, 165, 32, 0.25)'; // light yellow
                                          if (conflictingCaseStatus === 'CLOSED' || conflictingCaseStatus === 'ARCHIVED') return 'rgba(244, 67, 54, 0.12)'; // light red
                                          return 'rgba(244, 67, 54, 0.12)'; // fallback red for conflicts
                                        }
                                        return isClosed
                                          ? 'rgba(244, 67, 54, 0.12)' // darker red on hover for closed/archived
                                          : 'grey.100';
                                      })(),
                              },
                              */
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                {/* Hide expand button in demo mode */}
                                {!isDemoMode && (
                                  <IconButton
                                    size="small"
                                    onClick={toggleExpand}
                                    sx={{ mt: -0.5, flexShrink: 0 }}
                                  >
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                )}

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  {leadNames.length > 0 ? (
                                    <>
                                      {leadNames.map((name: string, index: number) => (
                                        <Typography key={index} variant="body2" sx={{ fontWeight: 'medium', lineHeight: 1.4 }}>
                                          {name}
                                        </Typography>
                                      ))}
                                      {hasMore && (
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', lineHeight: 1.4, fontStyle: 'italic' }}>
                                          {portfolioName.match(/\+\d+ more/)?.[0] || ''}
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {portfolioName}
                                    </Typography>
                                  )}
                                  {/* Hide owner counts in demo mode */}
                                  {!isDemoMode && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      {totalOwners} Potential Co-Defendants
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const states = Array.isArray(row.metadata?.states) ? row.metadata.states : [];
                                if (isDemoMode && states.length > 0) {
                                  // Randomize states in demo mode using row id as seed
                                  const allStates = ['CA', 'TX', 'FL', 'NY', 'IL', 'PA', 'OH', 'GA', 'NC', 'MI', 'NJ', 'VA', 'WA', 'AZ', 'MA'];
                                  const hash = row.id?.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0) || 0;
                                  const numStates = Math.min(states.length, allStates.length);
                                  const startIdx = hash % allStates.length;
                                  const fakeStates = [];
                                  for (let i = 0; i < numStates; i++) {
                                    fakeStates.push(allStates[(startIdx + i) % allStates.length]);
                                  }
                                  return fakeStates.join(', ');
                                }
                                return states.join(', ') || '';
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const ccns = row.metadata?.ccns || [];
                                const count = Array.isArray(ccns) ? ccns.length : 0;
                                const ccnList = Array.isArray(ccns) ? ccns.join(', ') : '';

                                // In demo mode, hide CCN list in tooltip
                                const tooltipTitle = isDemoMode ? '' : (count > 0 ? `CCNs: ${ccnList}` : 'No facilities');

                                return (
                                  <Tooltip title={tooltipTitle} arrow>
                                    <Chip
                                      label={`${count} ${count === 1 ? 'Facility' : 'Facilities'}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ cursor: isDemoMode ? 'default' : 'help' }}
                                    />
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const num = parseFloat(String(row.total_billed));
                                const formatted = isNaN(num) ? '-' : `$${num.toLocaleString()}`;
                                return <Box sx={{ fontWeight: 600 }}>{formatted}</Box>;
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const num = parseFloat(String(row.total_paid));
                                const formatted = isNaN(num) ? '-' : `$${num.toLocaleString()}`;
                                return <Box sx={{ fontWeight: 600 }}>{formatted}</Box>;
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const canOpenInvestigation = row.can_open_investigation;

                                // In demo mode, always show Investigate button regardless of case status
                                if (isDemoMode) {
                                  return (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        await openInvestigation(row.id);
                                      }}
                                      sx={{
                                        '&:hover': {
                                          backgroundColor: 'primary.main',
                                          color: 'primary.contrastText',
                                          borderColor: 'primary.main',
                                        },
                                      }}
                                    >
                                      Investigate
                                    </Button>
                                  );
                                }

                                const hasConflict = row.has_ccn_conflict;

                                if (hasActiveCase) {
                                  // Determine button color based on case status
                                  let buttonColor: 'success' | 'warning' | 'error' | 'info' = 'info';
                                  let customSx = {};
                                  if (row.active_case_status === 'FILED') {
                                    buttonColor = 'success';
                                  } else if (row.active_case_status === 'UNDER_INVESTIGATION') {
                                    buttonColor = 'info'; // Use info as base
                                    customSx = {
                                      backgroundColor: '#DAA520', // goldish
                                      color: 'white', // white text for better consistency
                                      '&:hover': {
                                        backgroundColor: '#B8860B', // darker gold on hover
                                      },
                                    };
                                  } else if (row.active_case_status === 'IN_REVIEW') {
                                    buttonColor = 'warning'; // Use warning as base
                                    customSx = {
                                      backgroundColor: '#ed6c02', // orange
                                      color: 'white',
                                      '&:hover': {
                                        backgroundColor: '#e65100', // darker orange on hover
                                      },
                                    };
                                  } else if (row.active_case_status === 'CLOSED' || row.active_case_status === 'ARCHIVED') {
                                    buttonColor = 'error';
                                  }

                                  return (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      color={buttonColor}
                                      sx={customSx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewCase(row.active_case_id);
                                      }}
                                    >
                                      View Case
                                    </Button>
                                  );
                                }

                                // Show View Case for CCN conflicts
                                if (hasConflict && row.conflicting_case && row.conflicting_case.id !== row.any_case_id) {
                                  const tooltipText = row.conflicting_case.lead_defendant || row.conflicting_case.name
                                    ? `This Lead contains facilities in an investigation. Case: ${row.conflicting_case.lead_defendant || row.conflicting_case.name}`
                                    : "This Lead contains facilities in an investigation";

                                  // Determine button color based on conflicting case status
                                  let conflictButtonColor: 'success' | 'warning' | 'error' | 'info' = 'info';
                                  let conflictCustomSx = {};
                                  if (row.conflicting_case.status === 'FILED') {
                                    conflictButtonColor = 'success';
                                  } else if (row.conflicting_case.status === 'UNDER_INVESTIGATION') {
                                    conflictButtonColor = 'info'; // Use info as base
                                    conflictCustomSx = {
                                      backgroundColor: '#DAA520', // goldish
                                      color: 'white', // white text for better consistency
                                      '&:hover': {
                                        backgroundColor: '#B8860B', // darker gold on hover
                                      },
                                    };
                                  } else if (row.conflicting_case.status === 'IN_REVIEW') {
                                    conflictButtonColor = 'warning'; // Use warning as base
                                    conflictCustomSx = {
                                      backgroundColor: '#ed6c02', // orange
                                      color: 'white',
                                      '&:hover': {
                                        backgroundColor: '#e65100', // darker orange on hover
                                      },
                                    };
                                  } else if (row.conflicting_case.status === 'CLOSED' || row.conflicting_case.status === 'ARCHIVED') {
                                    conflictButtonColor = 'error';
                                  }

                                  return (
                                    <Tooltip title={tooltipText} arrow>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        color={conflictButtonColor}
                                        sx={conflictCustomSx}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewCase(row.conflicting_case.id);
                                        }}
                                      >
                                        View Case
                                      </Button>
                                    </Tooltip>
                                  );
                                }

                                // Check if target is closed OR has a closed case that prevents new investigations
                                const hasClosedCase = row.any_case_id && row.any_case_status === 'CLOSED';

                                if (isClosed || (hasClosedCase && !canOpenInvestigation)) {
                                  if (hasClosedCase) {
                                    // Show View Case button for targets with closed cases
                                    return (
                                      <Tooltip
                                        title={isClosed && row.closure_note ? `Archived: ${row.closure_note}` : ''}
                                        arrow
                                      >
                                        <Button
                                          variant="contained"
                                          size="small"
                                          color="error"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewCase(row.any_case_id);
                                          }}
                                        >
                                          View Case
                                        </Button>
                                      </Tooltip>
                                    );
                                  } else {
                                    // Show only Unarchive button for archived targets without cases
                                    return (
                                      <Tooltip title={row.closure_note ? `Archived: ${row.closure_note}` : ''} arrow>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          color="error"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            unarchiveTarget(row);
                                          }}
                                        >
                                          Unarchive
                                        </Button>
                                      </Tooltip>
                                    );
                                  }
                                }

                                // For open targets, show Investigate button (demo mode) or Actions menu (normal mode)
                                const tooltipText = hasConflict
                                  ? "Some facilities in this ownership group are already in another investigation"
                                  : "";

                                if (isDemoMode) {
                                  return (
                                    <Tooltip title={tooltipText} arrow>
                                      <span>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            await openInvestigation(row.id);
                                          }}
                                          disabled={!canOpenInvestigation}
                                          sx={{
                                            opacity: canOpenInvestigation ? 1 : 0.6,
                                            '&:hover': {
                                              backgroundColor: 'primary.main',
                                              color: 'primary.contrastText',
                                              borderColor: 'primary.main',
                                            },
                                          }}
                                        >
                                          Investigate
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  );
                                }

                                // Normal mode: show actions menu
                                return (
                                  <>
                                    <Tooltip title={tooltipText} arrow>
                                      <span>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          endIcon={<MoreVert />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTarget(row);
                                            setMenuAnchorEl(e.currentTarget);
                                          }}
                                          disabled={!canOpenInvestigation}
                                          sx={{
                                            opacity: canOpenInvestigation ? 1 : 0.6,
                                            backgroundColor: canOpenInvestigation ? 'inherit' : 'rgba(0, 0, 0, 0.04)'
                                          }}
                                        >
                                          Actions
                                        </Button>
                                      </span>
                                    </Tooltip>

                                    <Menu
                                      anchorEl={menuAnchorEl}
                                      open={Boolean(menuAnchorEl) && selectedTarget?.id === row.id}
                                      onClose={() => {
                                        setMenuAnchorEl(null);
                                        setSelectedTarget(null);
                                      }}
                                    >
                                      <MenuItem
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setMenuAnchorEl(null);
                                          await openInvestigation(row.id);
                                        }}
                                        disabled={!canOpenInvestigation}
                                      >
                                        <ListItemIcon>
                                          <Search fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary="Open Review" />
                                      </MenuItem>
                                      <MenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleArchiveClick(row);
                                        }}
                                      >
                                        <ListItemIcon>
                                          <Archive fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary="Archive Lead" />
                                      </MenuItem>
                                    </Menu>
                                  </>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded Content Row */}
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ py: 0, border: 0, backgroundColor: 'grey.25' }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                                      Ownership Details
                                    </Typography>
                                    
                                    {ownerGroups.map((group: any, groupIndex: number) => {
                                      const groupExpandKey = `${row.id}-group-${groupIndex}`;
                                      const isGroupExpanded = expandedRows.has(groupExpandKey);
                                      
                                      return (
                                        <Box key={groupIndex} sx={{ mb: 3 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Chip 
                                              label={group.role_suggestion === 'LEAD' ? 'Lead Ownership Group' : 'Ownership Group'} 
                                              size="medium" 
                                              color={group.role_suggestion === 'LEAD' ? 'primary' : 'default'}
                                              sx={{ fontWeight: 'bold' }}
                                            />
                                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                              Has ownership in <strong>{group.coverage_count}</strong> of <strong>{row.metadata?.ccns?.length}</strong> facilities 
                                              (<strong>{Math.round((group.coverage_pct || 0) * 100)}%</strong> coverage)
                                            </Typography>
                                          </Box>
                                          
                                          {/* Owners in this group */}
                                          <TableContainer component={Paper} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                                  <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Owner Name</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Type</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Owner ID</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Address</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {group.members?.map((member: any, memberIndex: number) => (
                                                  <TableRow 
                                                    key={memberIndex}
                                                    sx={{ 
                                                      '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                                                      '&:hover': { backgroundColor: 'action.hover' }
                                                    }}
                                                  >
                                                    <TableCell sx={{ fontWeight: 'medium' }}>
                                                      {member.owner?.name || 'Unknown'}
                                                    </TableCell>
                                                    <TableCell>
                                                      <Chip 
                                                        label={member.owner?.type === 'I' ? 'Individual' : 'Organization'} 
                                                        size="small"
                                                        color={member.owner?.type === 'I' ? 'secondary' : 'primary'}
                                                        variant="outlined"
                                                      />
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace' }}>
                                                      {member.owner?.id || 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                      {member.owner?.address_line_1_owner ? (
                                                        <Box>
                                                          <Typography variant="body2">
                                                            {[
                                                              member.owner.address_line_1_owner,
                                                              member.owner.address_line_2_owner
                                                            ].filter(Boolean).join(', ')}
                                                          </Typography>
                                                          <Typography variant="caption" color="text.secondary">
                                                            {[
                                                              member.owner.city_owner,
                                                              member.owner.state_owner,
                                                              member.owner.zip_code_owner
                                                            ].filter(Boolean).join(', ')}
                                                          </Typography>
                                                        </Box>
                                                      ) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                          No address on file
                                                        </Typography>
                                                      )}
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                          
                                          {/* Toggle between Current Portfolio Facilities and Other Related Facilities */}
                                          {((group.facilities && group.facilities.length > 0) || group.members?.some((member: any) => {
                                            const totalOtherFacilities = (member.outside_facilities?.length || 0) + (member.outside_unassigned_facilities?.length || 0);
                                            return totalOtherFacilities > 0 || (member.outside_by_portfolio?.length || 0) > 0;
                                          })) && (
                                            <Box sx={{ mb: 2 }}>
                                              {/* Toggle Buttons */}
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <Button
                                                  variant={!expandedRows.has(`${row.id}-other-mode-${groupIndex}`) ? "contained" : "outlined"}
                                                  size="small"
                                                  onClick={() => {
                                                    const newExpanded = new Set(expandedRows);
                                                    newExpanded.delete(`${row.id}-other-mode-${groupIndex}`);
                                                    newExpanded.add(groupExpandKey);
                                                    setExpandedRows(newExpanded);
                                                  }}
                                                  color="primary"
                                                >
                                                  View Facilities ({group.facilities?.length || 0})
                                                </Button>
                                                {group.members?.some((member: any) => {
                                                  const totalOtherFacilities = (member.outside_facilities?.length || 0) + (member.outside_unassigned_facilities?.length || 0);
                                                  return totalOtherFacilities > 0 || (member.outside_by_portfolio?.length || 0) > 0;
                                                }) && (
                                                  <Button
                                                    variant={expandedRows.has(`${row.id}-other-mode-${groupIndex}`) ? "contained" : "outlined"}
                                                    size="small"
                                                    onClick={() => {
                                                      const newExpanded = new Set(expandedRows);
                                                      newExpanded.delete(groupExpandKey);
                                                      newExpanded.add(`${row.id}-other-mode-${groupIndex}`);
                                                      setExpandedRows(newExpanded);
                                                    }}
                                                    color="warning"
                                                  >
                                                    Other Related Facilities
                                                  </Button>
                                                )}
                                              </Box>
                                              
                                              <Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
                                                <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider', ml: 4 }}>
                                                  <Table size="small">
                                                    <TableHead>
                                                      <TableRow sx={{ backgroundColor: 'info.main' }}>
                                                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>CCN</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Facility Name</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>DBA Name</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>State</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>City</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Billed</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Paid</TableCell>
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {group.facilities.map((facility: any, facilityIndex: number) => (
                                                        <TableRow 
                                                          key={facilityIndex}
                                                          sx={{ 
                                                            '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                                                            '&:hover': { backgroundColor: 'info.light', color: 'info.contrastText' }
                                                          }}
                                                        >
                                                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                                            {facility.ccn}
                                                          </TableCell>
                                                          <TableCell sx={{ fontWeight: 'medium' }}>
                                                            {facility.org_name || 'Unknown'}
                                                          </TableCell>
                                                          <TableCell>
                                                            {facility.dba_name || (
                                                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                No DBA
                                                              </Typography>
                                                            )}
                                                          </TableCell>
                                                          <TableCell>
                                                            <Chip 
                                                              label={facility.state || 'Unknown'} 
                                                              size="small" 
                                                              color="secondary" 
                                                              variant="outlined"
                                                            />
                                                          </TableCell>
                                                          <TableCell>
                                                            {facility.city || (
                                                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                Unknown
                                                              </Typography>
                                                            )}
                                                          </TableCell>
                                                          <TableCell sx={{ fontWeight: 600 }}>
                                                            ${parseFloat(facility.total_billed || '0').toLocaleString()}
                                                          </TableCell>
                                                          <TableCell sx={{ fontWeight: 600 }}>
                                                            ${parseFloat(facility.total_paid || '0').toLocaleString()}
                                                          </TableCell>
                                                        </TableRow>
                                                      ))}
                                                    </TableBody>
                                                  </Table>
                                                </TableContainer>
                                              </Collapse>
                                              
                                              {/* Other Related Facilities Section */}
                                              <Collapse in={expandedRows.has(`${row.id}-other-mode-${groupIndex}`)} timeout="auto" unmountOnExit>
                                                <Box sx={{ ml: 2 }}>
                                                  {group.members?.map((member: any, memberIndex: number) => {
                                                    const totalOtherFacilities = (member.outside_facilities?.length || 0) + (member.outside_unassigned_facilities?.length || 0);
                                                    const hasOtherPortfolios = (member.outside_by_portfolio?.length || 0) > 0;
                                                    
                                                    if (totalOtherFacilities === 0 && !hasOtherPortfolios) return null;
                                                    
                                                    return (
                                                      <Box key={memberIndex} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 2, backgroundColor: 'grey.50' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                          {member.owner.name} - Other Facilities ({totalOtherFacilities}{hasOtherPortfolios && totalOtherFacilities > 0 ? ` + ${member.outside_by_portfolio.length} portfolios` : hasOtherPortfolios ? `${member.outside_by_portfolio.length} portfolios` : ''})
                                                        </Typography>
                                                        
                                                        {/* Facilities in other portfolios */}
                                                        {member.outside_facilities && member.outside_facilities.length > 0 && (
                                                          <Box sx={{ mb: 2 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 1, display: 'block' }}>
                                                              In Other Portfolios ({member.outside_facilities.length}):
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                              {member.outside_facilities.map((facility: any, idx: number) => (
                                                                <Box key={idx} sx={{ p: 1, backgroundColor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                                                                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                                    {facility.dba_name || facility.org_name || 'Unknown'} <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>({facility.ccn})</Typography>
                                                                  </Typography>
                                                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                                    {facility.org_name && facility.dba_name && `Org: ${facility.org_name}`}
                                                                  </Typography>
                                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                    {facility.city}, {facility.state} • ${parseFloat(facility.total_billed || '0').toLocaleString()} billed
                                                                  </Typography>
                                                                </Box>
                                                              ))}
                                                            </Box>
                                                          </Box>
                                                        )}
                                                        
                                                        {/* Unassigned facilities */}
                                                        {member.outside_unassigned_facilities && member.outside_unassigned_facilities.length > 0 && (
                                                          <Box sx={{ mb: 2 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.main', mb: 1, display: 'block' }}>
                                                              Unassigned to Portfolios ({member.outside_unassigned_facilities.length}):
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                              {member.outside_unassigned_facilities.map((facility: any, idx: number) => (
                                                                <Box key={idx} sx={{ p: 1, backgroundColor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                                                                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                                    {facility.dba_name || facility.org_name || 'Unknown'} <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>({facility.ccn})</Typography>
                                                                  </Typography>
                                                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                                    {facility.org_name && facility.dba_name && `Org: ${facility.org_name}`}
                                                                  </Typography>
                                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                    {facility.city}, {facility.state} • ${parseFloat(facility.total_billed || '0').toLocaleString()} billed
                                                                  </Typography>
                                                                </Box>
                                                              ))}
                                                            </Box>
                                                          </Box>
                                                        )}
                                                        
                                                        {/* Portfolio summary */}
                                                        {member.outside_by_portfolio && member.outside_by_portfolio.length > 0 && (
                                                          <Box>
                                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main', mb: 1, display: 'block' }}>
                                                              Other Ownership Portfolios ({member.outside_by_portfolio.length}):
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                              {member.outside_by_portfolio.map((portfolio: any, idx: number) => (
                                                                <Box key={idx} sx={{ p: 1, backgroundColor: 'success.light', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                                                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                    {portfolio.scheme_target_name || `Portfolio (${portfolio.portfolio_uuid?.substring(0, 8)}...)`}
                                                                  </Typography>
                                                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                                                    {portfolio.count} facilities • ${parseFloat(portfolio.total_billed || '0').toLocaleString()} billed
                                                                  </Typography>
                                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                    CCNs: {portfolio.ccns?.join(', ') || 'N/A'}
                                                                  </Typography>
                                                                </Box>
                                                              ))}
                                                            </Box>
                                                          </Box>
                                                        )}
                                                      </Box>
                                                    );
                                                  })}
                                                </Box>
                                              </Collapse>
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    })}
                                    
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Pagination controls for owners view */}
                {view === 'owners' && ownersPaginationData && (
                  <TablePagination
                    component="div"
                    count={ownersPaginationData.total_count || 0}
                    page={ownersPage}
                    onPageChange={(_, newPage) => {
                      setOwnersPage(newPage);
                      // Scroll to top of table when changing pages
                      const tableContainer = document.querySelector('[data-testid="owners-table-container"]');
                      if (tableContainer) {
                        tableContainer.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    rowsPerPage={ownersPageSize}
                    onRowsPerPageChange={(e) => {
                      setOwnersPageSize(+e.target.value);
                      // Scroll to top of table when changing page size
                      const tableContainer = document.querySelector('[data-testid="owners-table-container"]');
                      if (tableContainer) {
                        tableContainer.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    showFirstButton
                    showLastButton
                    labelRowsPerPage="Ownership groups per page:"
                    sx={{
                      borderTop: '1px solid',
                      borderTopColor: 'divider',
                      backgroundColor: 'grey.25',
                      opacity: loadingRows ? 0.8 : 1,
                      pointerEvents: loadingRows ? 'none' : 'auto',
                    }}
                  />
                )}
              </TableContainer>
            ) : (
              <DataGrid
                rows={filteredRows}
                columns={columns}
                loading={loadingRows}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                pagination
                paginationMode="client"
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 50,
                    },
                  },
                }}
                /* DEMO EDIT (2026-05-20): row case-status coloring disabled for demo — restore getRowClassName below after demo
                getRowClassName={(params) => {
                  if (params.row.active_case_id) {
                    // Use case status for background color
                    if (params.row.active_case_status === 'FILED') return 'filed-case-row';
                    if (params.row.active_case_status === 'UNDER_INVESTIGATION') return 'under-investigation-case-row';
                    if (params.row.active_case_status === 'CLOSED' || params.row.active_case_status === 'ARCHIVED') return 'archived-case-row';
                    return 'active-case-row'; // fallback for other statuses
                  }
                  // Handle CCN conflicts - use the conflicting case's status for highlighting
                  if (params.row.has_ccn_conflict && params.row.conflicting_case && !params.row.active_case_id) {
                    const conflictingCaseStatus = params.row.conflicting_case.status;
                    if (conflictingCaseStatus === 'FILED') return 'filed-case-row';
                    if (conflictingCaseStatus === 'UNDER_INVESTIGATION') return 'under-investigation-case-row';
                    if (conflictingCaseStatus === 'CLOSED' || conflictingCaseStatus === 'ARCHIVED') return 'archived-case-row';
                    return 'conflict-row'; // fallback for conflicts
                  }
                  if (params.row.status === 'CLOSED') return 'archived-row';
                  return '';
                }}
                */
                sx={{
                  ...dataGridBoldHeaders,
                  // DEMO EDIT (2026-05-20): neutralize action buttons — white/outlined look, grey on hover (no status colors).
                  // Remove this '& .MuiButton-root' block to restore the green/gold/red status button colors after the demo.
                  '& .MuiButton-root': {
                    backgroundColor: '#ffffff !important',
                    color: 'rgba(0, 0, 0, 0.75) !important',
                    border: '1px solid rgba(0, 0, 0, 0.23) !important',
                    boxShadow: 'none !important',
                  },
                  '& .MuiButton-root:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06) !important',
                    border: '1px solid rgba(0, 0, 0, 0.4) !important',
                    boxShadow: 'none !important',
                  },
                  height: 700, // Reduced height for side-by-side layout
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '& .filed-case-row': {
                    backgroundColor: 'rgba(76, 175, 80, 0.08)', // light green
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.12)',
                    },
                  },
                  '& .under-investigation-case-row': {
                    backgroundColor: 'rgba(218, 165, 32, 0.15)', // light yellow
                    '&:hover': {
                      backgroundColor: 'rgba(218, 165, 32, 0.25)',
                    },
                  },
                  '& .archived-case-row': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)', // light red
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.12)',
                    },
                  },
                  '& .active-case-row': {
                    backgroundColor: 'rgba(33, 150, 243, 0.08)', // fallback blue
                    '&:hover': {
                      backgroundColor: 'rgba(33, 150, 243, 0.12)',
                    },
                  },
                  '& .archived-row': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)', // light red
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.12)', // darker red on hover
                    },
                  },
                  '& .conflict-row': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                    opacity: 0.6,
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.12)',
                    },
                  },
                  '& .MuiDataGrid-cell': {
                    borderRight: '1px solid',
                    borderRightColor: 'grey.200',
                    '&:focus': {
                      outline: 'none',
                    },
                    '&:focus-within': {
                      outline: 'none',
                    },
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&.filed-case-row:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.12)',
                    },
                    '&.under-investigation-case-row:hover': {
                      backgroundColor: 'rgba(218, 165, 32, 0.25)',
                    },
                    '&.archived-case-row:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.12)',
                    },
                    '&.active-case-row:hover': {
                      backgroundColor: 'rgba(33, 150, 243, 0.12)',
                    },
                    '&.archived-row:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.12)', // darker red on hover
                    },
                  },
                }}
              />
            )}
          </CardContent>
        </Card>

        {/* Right: Documents & Templates */}
        <Card sx={{ flex: '1 1 35%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              📄 Litigation Dashboard
            </Typography>
            
            {templates.length === 0 ? (
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No documents configured for this scheme.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600, overflowY: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Document Name</strong></TableCell>
                      <TableCell><strong>View</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.flatMap((docGroup) => {
                      // In demo mode, hide certain sensitive documents and rename others
                      const hiddenInDemo = ['Prosecution Memo', 'Settlement Agreement', 'Litigation Support Memo', 'Defense Presentation'];
                      const renameInDemo: Record<string, string> = {
                        'First CID': 'First Subpoena',
                        'Second CID': 'Second Subpoena',
                      };
                      const filteredItems = isDemoMode
                        ? docGroup.items.filter((item: any) => !hiddenInDemo.includes(item.label))
                        : docGroup.items;

                      return filteredItems.map((item: any) => {
                        const displayLabel = isDemoMode && renameInDemo[item.label] ? renameInDemo[item.label] : item.label;
                        return (
                        <TableRow key={item.id} hover>
                          <TableCell sx={{
                            fontWeight: 'medium',
                            paddingLeft: item.doctype === 'EXHIBIT' ? 4 : 2
                          }}>
                            {displayLabel}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              {!item.is_auto_generated && item.filename && (
                                <Tooltip title="Download template">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadTemplate(item)}
                                    color="primary"
                                  >
                                    <Download fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {item.is_auto_generated && (
                                <Tooltip title="Auto-generated documents cannot be downloaded as templates">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled
                                    >
                                      <DescriptionIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                      });
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ───── Case Create Modal ───── */}
      <CaseDialog
        open={caseModalOpen}
        mode="create"
        values={defaultCaseValues}
        onClose={() => setCaseModalOpen(false)}
        onChange={(e) =>
          setDefaultCaseValues({ ...defaultCaseValues, [e.target.name]: e.target.value })
        }
        onSubmit={async () => {
          try {
            const response = await api.post('/cases/', defaultCaseValues, { headers });
            const newCaseId = response.data.id;
            setCaseModalOpen(false);
            setDefaultCaseValues(null);
            navigate(`/cases/${newCaseId}`);
          } catch (err) {
            console.error('Failed to create case', err);
          }
        }}
      />

      {/* ───── Archive Target Dialog ───── */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => {
          setArchiveDialogOpen(false);
          setSelectedTarget(null);
          setArchiveNote('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Archive Lead: {selectedTarget?.affiliation_name || selectedTarget?.org_name || selectedTarget?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for archiving this lead. This action will mark the lead as closed.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Archive Note"
            placeholder="Explain why this target is being archived..."
            value={archiveNote}
            onChange={(e) => setArchiveNote(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setArchiveDialogOpen(false);
              setSelectedTarget(null);
              setArchiveNote('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={archiveTarget}
            disabled={!archiveNote.trim() || archiveLoading}
            variant="contained"
            color="error"
            startIcon={archiveLoading ? <CircularProgress size={16} /> : <Archive />}
          >
            {archiveLoading ? 'Archiving...' : 'Archive Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───── Section: Data Sources (moved to bottom) ───── */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            📊 Data Sources
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Dataset</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>View</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Arcadia Medicare SNF Claims</TableCell>
                  <TableCell>Daily and Weekly Medicare Claims datasets for SNFs. Identifies bulk SNF admissions, total billed and paid by Medicare per facility</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" disabled>
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Nursing Home COVID-19</TableCell>
                  <TableCell>The data reported by nursing homes to the CDC, regarding their in facility COVID-19 numbers.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/covid-19/covid-19-nursing-home-data" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Skilled Nursing Facility Enrollments</TableCell>
                  <TableCell>Information on Skilled Nursing Facilities currently enrolled in Medicare. Provides names, addresses, and other related data for each SNF.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-enrollments" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Skilled Nursing Facility All Owners</TableCell>
                  <TableCell>Provides information on all owners of SNFs currently enrolled in Medicare. </TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-all-owners" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>SNF Ownership Change</TableCell>
                  <TableCell>List of every ownership change for all SNFs.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-change-of-ownership" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Payroll Based Journal Daily Non-Nurse Staffing</TableCell>
                  <TableCell>Provides daily hours for each staff type (non-nursing) per facility.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/quality-of-care/payroll-based-journal-daily-non-nurse-staffing" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Payroll Based Journal Daily Nurse Staffing</TableCell>
                  <TableCell>Provides daily hours for each staff type (nursing) per facility.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/quality-of-care/payroll-based-journal-daily-nurse-staffing" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Loading Dialog for Opening Investigation */}
      <Dialog
        open={openingInvestigation}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            textAlign: 'center',
            py: 4
          }
        }}
      >
        <DialogContent>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Opening Investigation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Creating case and preparing data...
          </Typography>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
